import apiClient from './client';
import type {
  PredictedHandDetail,
  PredictedHandIndexResponse,
  PredictionReviewState,
  ReviewStateResponse,
} from '../types/predictedJeffBids';

/**
 * Feature 025 — "Predicted Jeff's Bids by AI" (categories and sub-categories since 032).
 * Read-mostly: the writes are the reviewer's own bookmark and the shared correction explanation
 * on mismatched hands.
 *
 * The index and the per-hand detail are separate calls on purpose: the index carries every
 * loaded prediction plus the category tree so navigation and filtering are instant, while the
 * reasoning text arrives one prediction at a time rather than as a multi-megabyte payload before
 * the first hand appears.
 */
export const predictedJeffBidsApi = {
  /** The category tree plus every loaded prediction (one entry per sub-category a deal is in). */
  getIndex: async (): Promise<PredictedHandIndexResponse> => {
    const response = await apiClient.get<PredictedHandIndexResponse>('/predicted-jeff-bids/hands');
    return response.data;
  },

  /** Full review detail for one prediction. Rejects with a 404 when that pair was never loaded. */
  getHand: async (subcategoryId: number, tableResultId: number): Promise<PredictedHandDetail> => {
    const response = await apiClient.get<PredictedHandDetail>(
      `/predicted-jeff-bids/hands/${subcategoryId}/${tableResultId}`
    );
    return response.data;
  },

  /**
   * The deal-only form, for a link that names the deal without its sub-category: the deal's
   * first prediction by display order. Rejects with a 404 when the deal is in no sub-category.
   */
  getHandByDeal: async (tableResultId: number): Promise<PredictedHandDetail> => {
    const response = await apiClient.get<PredictedHandDetail>(
      `/predicted-jeff-bids/hands/${tableResultId}`
    );
    return response.data;
  },

  /**
   * The caller's bookmarks, one per filter combination they have visited, newest-touched first.
   * Empty `states` when they have never opened the page. The page reopens at `states[0]` and keeps
   * the rest to resume each scope when a dropdown is switched.
   */
  getReviewStates: async (): Promise<ReviewStateResponse> => {
    const response = await apiClient.get<ReviewStateResponse>('/predicted-jeff-bids/review-state');
    return response.data;
  },

  /**
   * Saves the caller's bookmark for one scope (keyed server-side by user, category, sub-category
   * or all, and outcome). Called on every hand and filter change, so failures are the caller's to
   * swallow: losing a bookmark write must never interrupt the reviewing.
   */
  putReviewState: async (state: PredictionReviewState): Promise<void> => {
    await apiClient.put('/predicted-jeff-bids/review-state', state);
  },

  /**
   * Saves (or, with null, clears) the shared correction for one mismatched prediction. Failures
   * are NOT swallowed — unlike the bookmark, the editor needs to see a rejection so the draft is
   * kept and the error shown. Rejects 404 (unknown pair) or 409 (exact-match prediction).
   */
  putCorrection: async (
    subcategoryId: number,
    tableResultId: number,
    correction: string | null
  ): Promise<void> => {
    await apiClient.put(
      `/predicted-jeff-bids/hands/${subcategoryId}/${tableResultId}/correction`,
      { correction }
    );
  },
};
